import { Switch } from '../../../../components';
import { QuestionView } from '../../../logbook/views/QuestionView';
import {
    BulletList,
    Card,
    CardType,
    Exercise,
    GhostButton,
    LabelBody,
    LabelBodySmall,
    LabelDisplay1,
    LabelDisplay2,
    ProgressBar,
    SecondaryButton,
    Slider,
    Spacer,
    Textarea,
} from '../../components';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { ExerciseStatus } from '../../interfaces';
import React from 'react';
import { normalizeInput } from '../../useSyncedState';
import {
    IllustrationPenPaper,
    Module7Exercise1Illustration3,
    SealIcon,
} from '../../icons';
import { FeedbackText } from '../../components/Slider';

const mentalEnergyKey = 'mentalEnergy';
const reflectionKey = 'reflection';

export const exerciseData313: ExerciseData = {
    ExerciseView: Module3Exercise1Part3,
    dataKeys: [reflectionKey],
    questions: [
        {
            Answers: (props) => (
                <QuestionView
                    {...props}
                    title="Hvordan er din mentale energi?"
                    description="1 = ufokuseret og 4 = fokuseret"
                    answerTitle="Din score"
                />
            ),
            dataKeys: [mentalEnergyKey],
        },
        {
            Answers: (props) => (
                <QuestionView
                    {...props}
                    title="Hvornår er jeg tilgængelig?"
                    description={
                        <BulletList
                            items={[
                                'Bruger jeg min mobil mere, når jeg er urolig, end når jeg er glad?',
                                'Hvor hurtigt føler jeg, at jeg skal reagere, hvis nogen skriver til mig?',
                                'Hvad er det værste, der ville ske, hvis jeg ikke svarer med det samme eller ikke er opdateret?',
                            ]}
                        />
                    }
                />
            ),
            dataKeys: [reflectionKey],
        },
    ],
};

const numberOfPreviousPages = 7;

function Module3Exercise1Part3({ exercise, onContinue }: ExerciseViewProps) {
    return (
        <Exercise title="Mental energi">
            <ProgressBar
                current={exercise.CurrentPage + numberOfPreviousPages}
                total={11}
            />
            <Switch
                cases={{
                    1: (
                        <Page1
                            sliderValue={
                                exercise.Data[mentalEnergyKey] != null
                                    ? Number(exercise.Data[mentalEnergyKey])
                                    : null
                            }
                            onFinish={() => {
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: false,
                                });
                            }}
                            onContinue={(sliderValue) => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                    data: {
                                        [mentalEnergyKey]: normalizeInput(
                                            sliderValue.toString(),
                                        ),
                                    },
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            reflectionText={exercise.Data[reflectionKey] ?? ''}
                            onContinue={(reflectionText) => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                    data: {
                                        [reflectionKey]:
                                            normalizeInput(reflectionText),
                                    },
                                });
                            }}
                        />
                    ),
                    3: (
                        <Page3
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: '3.1.4',
                                });
                            }}
                        />
                    ),
                }}
                value={exercise.CurrentPage}
                default={1}
            />
        </Exercise>
    );
}

const feedbackTexts: { [key: number]: FeedbackText } = {
    1: {
        title: 'Du har generelt svært ved at holde fokus',
        text: 'Start med små skridt og se om der fx er notifikationer eller støj, du kan fjerne.',
    },
    2: {
        title: 'Du har ofte svært ved at holde fokus',
        text: 'Tænk over om der er et tidspunkt på dagen, hvor du føler dig mere klar i hovedet. Prøv at udnytte dette tidspunkt til at arbejde med din mentale energi.',
    },
    3: {
        title: 'Du er god til at holde fokus',
        text: 'Der er god mental energi - og måske kan du styrke det, der virker godt for dig?',
    },
    4: {
        title: 'Du har let ved at holde fokus',
        text: 'Du har god mental energi. Ved at forstå hvad der fungerer for dig, så er det nemmere at holde det gode niveau. Hvad gør du mon, der virker for dig?',
    },
};
type Page1Props = {
    sliderValue: number | null;
    onFinish: () => void;
    onContinue: (sliderValue: number) => void;
};
function Page1({
    sliderValue: _sliderValue,
    onFinish,
    onContinue,
}: Page1Props) {
    const [sliderValue, setSliderValue] = React.useState<number | null>(
        _sliderValue,
    );
    return (
        <>
            <LabelDisplay2>Vurder din mentale energi</LabelDisplay2>
            <LabelBody className="my-10">
                Mental energi handler om evnen til at holde fokus og være
                nærværende. Er din mentale energi lav, bliver du måske let
                distraheret eller har svært ved at holde fokus.
            </LabelBody>
            <LabelBody>
                Reflektér over dit liv som helhed: Hvordan er din evne til at
                fokusere og være til stede? Bliver du ofte distraheret?
            </LabelBody>
            <Slider
                className="mb-30 mt-20"
                title="Hvordan er din mentale energi?"
                description="1 = ufokuseret og 4 = fokuseret"
                value={sliderValue}
                setValue={setSliderValue}
                feedbackTexts={sliderValue ? feedbackTexts[sliderValue] : null}
                backgroundImage={'/images/slider-background-3.svg'}
                min={0}
                hideZeroLabel={true}
            />
            <Spacer />
            <SecondaryButton
                disabled={!sliderValue}
                className="mb-20 mt-30"
                onClick={() => onContinue(sliderValue)}
            >
                Fortsæt til tippet
            </SecondaryButton>
            <GhostButton onClick={onFinish}>Vender tilbage senere</GhostButton>
        </>
    );
}

type Page2Props = {
    reflectionText: string;
    onContinue: (reflectionText: string) => void;
};
function Page2({ reflectionText: _reflectionText, onContinue }: Page2Props) {
    const [reflectionText, setReflectionText] = React.useState(_reflectionText);
    return (
        <>
            <LabelDisplay2>Hvornår er jeg tilgængelig?</LabelDisplay2>
            <LabelBody className="my-10">
                Det er helt normalt, at vores evne til at fokusere svinger i
                løbet af dagen. Én ting, der særligt kan stjæle vores
                opmærksomhed og tid, er din telefon.
            </LabelBody>
            <LabelBody className="mt-10">
                Følelsen af altid at skulle være tilgængelig kan tære på din
                mentale energi. Du kan få sværere ved at koncentrere dig og
                nogle gange kan det også påvirke dig negativt følelsesmæssigt.
            </LabelBody>
            <Card className="my-30">
                <IllustrationPenPaper />
                <LabelDisplay1 color="text-dark-red" className="mb-10 mt-20">
                    Refleksion
                </LabelDisplay1>
                <LabelBody className="mb-10">
                    Prøv at reflektere over hvordan det påvirker dig at være
                    tilgængelig næsten hele tiden. Her er nogle spørgsmål til
                    inspiration.
                </LabelBody>
                <LabelBody>
                    <BulletList
                        items={[
                            'Hvor meget af dagen er jeg tilgængelig for andre mennesker?',
                            'Bruger jeg min mobil mere når jeg er urolig end når jeg er glad?',
                            'Hvor hurtigt føler jeg, at jeg skal reagere på, hvis nogen skriver til mig?',
                            'Kan jeg tillade mig at være utilgængelig?',
                            'Hvad er det værste der ville ske, hvis jeg ikke svarer med det samme eller ikke er opdateret?',
                        ]}
                    />
                </LabelBody>
                <Textarea
                    placeholder="Skriv her..."
                    text={reflectionText}
                    onChangeText={setReflectionText}
                />
                <LabelBody className="mt-10">
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton
                className="mt-30"
                onClick={() => onContinue(reflectionText)}
            >
                Fortsæt
            </SecondaryButton>
        </>
    );
}

type Page3Props = {
    onContinue: () => void;
};
function Page3({ onContinue }: Page3Props) {
    return (
        <>
            <LabelDisplay2>Eksperimenter med dit fokus</LabelDisplay2>
            <LabelBody className="my-10">
                Hver gang du mister fokus og bliver distraheret, koster det
                energi at komme tilbage på sporet. Men den gode nyhed er, at
                evnen til at fokusere er noget, du kan træne – ligesom en
                muskel!
            </LabelBody>
            <Card type={CardType.Grey}>
                <Module7Exercise1Illustration3 className="mx-auto mb-20" />
                <LabelDisplay1>Hold dit fokus med Pomodoro</LabelDisplay1>
                <LabelBody className="mb-20 mt-10">
                    <BulletList
                        items={[
                            'Start med at vælge én opgave, f.eks. rydde op, lave lektier, læse en bog...',
                            'Indstil tiden på din telefon eller et æggeur. 25 minutters arbejdstid er max tid for fokus. Hvis det virker som meget, så start med kortere tid, f.eks. 10 min.',
                            'Læg mobilen væk og fokuser udelukkende på din valgte opgave.',
                            'Hold 5 minutters pause.',
                            'Gentag indtil du har været gennem 4 runder, og hold herefter en længere pause.',
                        ]}
                    />
                </LabelBody>
            </Card>
            <div className="mb-10 mt-30 flex flex-row">
                <SealIcon />
                <LabelBodySmall className="ml-10">Fun fact!</LabelBodySmall>
            </div>
            <LabelBody>
                Pomodoro betyder tomat på italiensk. Metoden bliver kaldt
                “Pomodoro”, fordi den blev opfundet af en italiener, der brugte
                et æggeur formet som en tomat til at holde styr på tiden.
            </LabelBody>
            <Spacer />
            <SecondaryButton className="mt-30" onClick={onContinue}>
                Fortsæt
            </SecondaryButton>
        </>
    );
}
